/**
 * Production's pre-TRUS-762 version, kept verbatim for orgs WITHOUT the
 * `member_profile_v2` capability (they keep the previous member profile).
 * Do not add features here; remove once every org is on member_profile_v2.
 */
/**
 * Programs tab — TRUS-378 / P3.
 *
 * A related list of the member's Program Enrollments (program · plan · status ·
 * assigned staff · units used/authorized), each row expanding to the full
 * configured field set.
 *
 * WHY THIS EXISTS
 * ---------------
 * The workbook's decision S1 is that status is per PROGRAM, not per member: a
 * member can be Open/Enrolled in CHW Benefits and Closed at Outreach in APS at
 * the same time. That is impossible to show on a member profile that carries a
 * single enrollment_status, which is what this tab replaces.
 *
 * THE FIELD SET IS NOT DECLARED HERE
 * ----------------------------------
 * Every field, label, option list, required flag and conditional rule comes
 * from GET /api/program-enrollments/field-config, which reads com_fields. There
 * is deliberately no literal list of the 31 fields anywhere in this file: an
 * admin adding a 32nd makes it appear without a frontend release, and one that
 * gets retired stops rendering. Hard-coding them is exactly the drift that
 * broke Maui's intake.
 *
 * Conditional visibility runs through the shared isFieldVisible() so a rule
 * behaves identically here, on the member edit form and on the public intake
 * page. Some rules key off NATIVE columns (`status`, `program`) rather than
 * custom fields, so the evaluation context aliases those in — see evalContext.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, ChevronDown, ChevronRight, Clock, History, Loader2, Plus, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { isFieldVisible } from '@/components/forms/fieldVisibility';
import { formatDateOnly, toDateInputValue } from '@/utils/dateTime';
import {
  programEnrollmentsAPI,
  type CustomFieldValueInput,
  type EnrollableProgram,
  type EnrollmentFieldCategory,
  type EnrollmentFieldDef,
  type ProgramEnrollment,
  type ProgramStatusConfig,
  type StatusHistoryEntry,
} from '@/services/api/programEnrollments';

interface MemberProgramsTabProps {
  memberId: number;
  /** View-only members and preview roles must not be able to write. */
  readOnly?: boolean;
}

const inputClass =
  'w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500';

/** Options tolerate both shapes the config has historically carried. */
function normalizeOptions(raw: EnrollmentFieldDef['field_options']): Array<{ label: string; value: string }> {
  if (!Array.isArray(raw)) return [];
  return raw.map((o: any) =>
    typeof o === 'string' ? { label: o, value: o } : { label: o?.label ?? o?.value ?? '', value: o?.value ?? o?.label ?? '' },
  );
}

/** Multi-select values arrive as an array from the API but may be a JSON string. */
function toArray(raw: any): string[] {
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string' && raw.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }
  return raw ? [String(raw)] : [];
}

function statusVariant(e: ProgramEnrollment): 'success' | 'warning' | 'danger' | 'secondary' {
  if (!e.program_status_code) return 'secondary';
  if (e.is_terminal) return e.program_status_code === 'closed_completed' ? 'success' : 'danger';
  if (e.program_status_code === 'open_enrolled') return 'success';
  return 'warning';
}

function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / 86_400_000);
}

// ---------------------------------------------------------------------------
// One dynamically-typed input. Mirrors the member profile's SectionEditField
// semantics so a field behaves the same on both surfaces.
// ---------------------------------------------------------------------------
function EnrollmentField({
  def,
  value,
  onChange,
  disabled,
}: {
  def: EnrollmentFieldDef;
  value: any;
  onChange: (v: any) => void;
  disabled: boolean;
}) {
  const type = (def.field_type || 'str').toLowerCase();
  const options = normalizeOptions(def.field_options);

  // A computed field with a formula is derived server-side. Showing an input
  // for it would invite a value that the next read silently overwrites.
  if (def.is_readonly || disabled) {
    const shown = Array.isArray(value) ? value.join(', ') : value;
    return (
      <div className="text-sm text-gray-800 dark:text-gray-200 py-1 min-h-[28px]">
        {shown === null || shown === undefined || shown === '' ? (
          <span className="text-gray-400">—</span>
        ) : (
          String(shown)
        )}
      </div>
    );
  }

  if (type === 'checkbox' && options.length === 0) {
    // A checkbox with no option list is a single boolean flag, not a group.
    const checked = value === true || value === 'true';
    return (
      <label className="flex items-center gap-2 text-sm py-1">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="text-gray-600 dark:text-gray-400">{checked ? 'Yes' : 'No'}</span>
      </label>
    );
  }

  if (type === 'bool' || type === 'toggle') {
    const checked = value === true || value === 'true';
    return (
      <label className="flex items-center gap-2 text-sm py-1">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="text-gray-600 dark:text-gray-400">{checked ? 'Yes' : 'No'}</span>
      </label>
    );
  }

  if (type === 'date' || type === 'fixed_date') {
    return (
      <input
        type="date"
        className={inputClass}
        value={toDateInputValue(value)}
        onChange={(e) => onChange(e.target.value || null)}
      />
    );
  }

  if (type === 'datetime') {
    return (
      <input
        type="datetime-local"
        className={inputClass}
        value={value ? String(value).slice(0, 16) : ''}
        onChange={(e) => onChange(e.target.value || null)}
      />
    );
  }

  if (type === 'int' || type === 'number') {
    return (
      <input
        type="number"
        min={0}
        className={inputClass}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      />
    );
  }

  if ((type === 'select' || type === 'radio') && options.length > 0) {
    return (
      <select className={inputClass} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  if (type === 'multi_select' || type === 'checkbox') {
    // Deliberately NOT gated on options.length: a multi_select with no options
    // must never fall through to the free-text input below — that turns a
    // constrained field into a text box (TRUS-289).
    const selected = toArray(value);
    return (
      <div className="flex flex-col gap-1 py-1">
        {options.length === 0 && <span className="text-xs text-gray-400">No options configured</span>}
        {options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={selected.includes(o.value)}
              onChange={(e) =>
                onChange(
                  e.target.checked ? [...selected, o.value] : selected.filter((v) => v !== o.value),
                )
              }
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    );
  }

  if (type === 'long_text') {
    return (
      <textarea
        rows={4}
        className={inputClass}
        value={value ?? ''}
        placeholder={def.placeholder_text || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <input
      type="text"
      className={inputClass}
      maxLength={def.max_length || undefined}
      value={value ?? ''}
      placeholder={def.placeholder_text || ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// ---------------------------------------------------------------------------
// The expanded form for one enrollment.
// ---------------------------------------------------------------------------
function EnrollmentDetail({
  enrollment,
  categories,
  statusConfig,
  readOnly,
  onSaved,
  onDeleted,
}: {
  enrollment: ProgramEnrollment;
  categories: EnrollmentFieldCategory[];
  statusConfig: ProgramStatusConfig | undefined;
  readOnly: boolean;
  onSaved: (updated: ProgramEnrollment) => void;
  onDeleted: (id: number) => void;
}) {
  const { t } = useAppTranslation();
  const [draft, setDraft] = useState<Record<string, any>>(enrollment.custom_fields || {});
  const [nativeDraft, setNativeDraft] = useState<Record<string, any>>({
    last_contact_date: enrollment.last_contact_date,
    interest_shown_date: enrollment.interest_shown_date,
    enrollment_date: enrollment.enrollment_date,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusTarget, setStatusTarget] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [history, setHistory] = useState<StatusHistoryEntry[] | null>(null);

  useEffect(() => {
    setDraft(enrollment.custom_fields || {});
    setNativeDraft({
      last_contact_date: enrollment.last_contact_date,
      interest_shown_date: enrollment.interest_shown_date,
      enrollment_date: enrollment.enrollment_date,
    });
  }, [enrollment]);

  /**
   * Some visibility_rules key off native columns rather than custom fields —
   * migration 659 seeds rules on `status` and `program`. Aliasing them in is
   * what makes those conditionals fire; without it a rule referencing `status`
   * resolves to undefined and the field renders unconditionally.
   */
  const evalContext = useMemo(
    () => ({
      ...draft,
      status: enrollment.program_status_code,
      program: enrollment.program_name,
    }),
    [draft, enrollment.program_status_code, enrollment.program_name],
  );

  const visibleCategories = useMemo(
    () =>
      categories
        .map((c) => ({
          ...c,
          fields: c.fields.filter(
            (f) =>
              !f.is_hidden &&
              isFieldVisible({ field_id: f.name, visibility_rules: f.visibility_rules ?? null }, null, evalContext),
          ),
        }))
        .filter((c) => c.fields.length > 0),
    [categories, evalContext],
  );

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Send only fields the server will accept a value for. Readonly/computed
      // fields are filtered here as well as server-side; sending them would be
      // silently dropped, which reads as a save that did nothing.
      const custom: CustomFieldValueInput[] = visibleCategories
        .flatMap((c) => c.fields)
        .filter((f) => !f.is_readonly)
        .map((f) => ({ field_name: f.name, value: draft[f.name] ?? null }));
      const updated = await programEnrollmentsAPI.update(enrollment.id, {
        native_fields: nativeDraft,
        custom_fields: custom,
      });
      onSaved(updated);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const applyStatus = async () => {
    if (!statusTarget) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await programEnrollmentsAPI.changeStatus(enrollment.id, {
        program_status_code: statusTarget,
        reason: statusReason || null,
      });
      onSaved(updated);
      setStatusTarget('');
      setStatusReason('');
      setHistory(null);
    } catch (e: any) {
      // A rejection here is the org's own transition graph talking. Surface the
      // server's message verbatim — it names the allowed statuses.
      setError(e?.response?.data?.detail || e?.message || 'Status change failed');
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = async () => {
    try {
      setHistory(await programEnrollmentsAPI.getStatusHistory(enrollment.id));
    } catch {
      setHistory([]);
    }
  };

  const remove = async () => {
    if (!window.confirm(t('programs.confirmRemoveEnrollment', 'Remove this program enrollment and all its values?')))
      return;
    try {
      await programEnrollmentsAPI.remove(enrollment.id);
      onDeleted(enrollment.id);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Delete failed');
    }
  };

  // Only offer moves the org's configuration permits. The server enforces the
  // same list, so this narrows the UI rather than being the gate itself.
  const nextStatuses = (statusConfig?.statuses || []).filter((s) =>
    enrollment.program_status_code
      ? (enrollment.allowed_next_statuses || []).includes(s.status_code)
      : true,
  );

  return (
    <div className="px-4 py-4 bg-gray-50 dark:bg-gray-900/40 border-t border-gray-200 dark:border-gray-700">
      {error && (
        <div className="mb-3 flex items-start gap-2 text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Status + timers ---------------------------------------------------- */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
            {t('programs.currentStatus', 'Current status')}
          </label>
          <Badge variant={statusVariant(enrollment)}>
            {enrollment.status_label || t('programs.noStatus', 'No status (interest only)')}
          </Badge>
        </div>
        {/* The platform lifecycle value, shown because it is NOT the badge above
            and the difference is load-bearing (TRUS-554).

            The badge is the org's configured program status (program_status_code).
            `enrollment_status` is the constrained platform value the rest of the
            backend gates on — document attribution, program field scoping, the
            caseload counts. A trigger derives one from the other, and parks at
            'pending' for as long as program_status_code is NULL, which for an org
            that does not run a program-status workflow is forever.

            Maui LTRG hit exactly that: the badge read "No status", the upload
            failed saying "Active enrollment not found", and the actual stored value
            was 'pending' — three vocabularies for one enrollment, none matching.
            Showing it is not an instruction to change it: these orgs have no
            configured statuses to move to, so there is no action here, only a fact
            that used to be invisible. */}
        {enrollment.enrollment_status && (
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              {t('programs.systemStatus', 'System status')}
            </label>
            <span
              className="inline-block text-xs text-gray-600 dark:text-gray-400 capitalize"
              title={t(
                'programs.systemStatusHelp',
                'The platform-level enrollment status. It is set automatically from the program status above, and is what other screens use to decide whether this enrollment is open.',
              )}
            >
              {enrollment.enrollment_status}
            </span>
          </div>
        )}
        {enrollment.status_effective_date && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t('programs.since', 'since')} {formatDateOnly(enrollment.status_effective_date)}
          </div>
        )}
        {/* Make the 90-day clock legible: this is the number the nightly job
            measures, scoped to THIS program. */}
        {enrollment.program_status_code === 'active_lead' && (
          <div className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
            <Clock className="w-3.5 h-3.5" />
            {(() => {
              const d = daysSince(enrollment.last_contact_date || enrollment.status_effective_date);
              return d === null
                ? t('programs.outreachClockUnanchored', 'Outreach clock not started')
                : `${d} ${t('programs.daysSinceContact', 'days since contact on this program')}`;
            })()}
          </div>
        )}
        {enrollment.program_status_code === 'open_enrolled' && enrollment.custom_fields?.renewal_date && (
          <div className="flex items-center gap-1 text-xs text-blue-700 dark:text-blue-400">
            <Clock className="w-3.5 h-3.5" />
            {t('programs.renewalDue', 'Renewal')} {formatDateOnly(enrollment.custom_fields.renewal_date)}
          </div>
        )}

        {!readOnly && nextStatuses.length > 0 && (
          <>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                {t('programs.changeStatus', 'Change status to')}
              </label>
              <select
                className={inputClass}
                value={statusTarget}
                onChange={(e) => setStatusTarget(e.target.value)}
              >
                <option value="">—</option>
                {nextStatuses.map((s) => (
                  <option key={s.status_code} value={s.status_code}>
                    {s.status_label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                {t('programs.statusReason', 'Reason (recorded in history)')}
              </label>
              <input
                type="text"
                className={inputClass}
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
              />
            </div>
            <Button size="sm" variant="secondary" disabled={!statusTarget || saving} onClick={applyStatus}>
              {t('common.apply', 'Apply')}
            </Button>
          </>
        )}
        {!readOnly && enrollment.program_status_code && nextStatuses.length === 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t('programs.terminalStatus', 'Terminal status — no further transitions configured')}
          </span>
        )}
      </div>

      {/* Configured fields, grouped by category ---------------------------- */}
      {visibleCategories.map((cat) => (
        <div key={cat.category_key} className="mb-5">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
            {cat.category_name}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
            {cat.fields.map((f) => (
              <div key={f.field_id}>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  {f.display_name}
                  {f.is_required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                <EnrollmentField
                  def={f}
                  value={draft[f.name]}
                  onChange={(v) => setDraft((d) => ({ ...d, [f.name]: v }))}
                  disabled={readOnly}
                />
                {f.help_text && <p className="mt-1 text-xs text-gray-400">{f.help_text}</p>}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Native dates the timers read -------------------------------------- */}
      <div className="mb-5">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
          {t('programs.contactDates', 'Contact & Dates')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3">
          {(
            [
              ['last_contact_date', t('programs.lastContactDate', 'Last Contact Date (this program)')],
              ['enrollment_date', t('programs.enrollmentDate', 'Enrollment Date')],
              ['interest_shown_date', t('programs.interestShownDate', 'Interest Shown Date')],
            ] as Array<[string, string]>
          ).map(([key, label]) => (
            <div key={key}>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{label}</label>
              {readOnly ? (
                <div className="text-sm text-gray-800 dark:text-gray-200 py-1">
                  {nativeDraft[key] ? formatDateOnly(nativeDraft[key]) : <span className="text-gray-400">—</span>}
                </div>
              ) : (
                <input
                  type="date"
                  className={inputClass}
                  value={toDateInputValue(nativeDraft[key])}
                  onChange={(e) => setNativeDraft((n) => ({ ...n, [key]: e.target.value || null }))}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {!readOnly && (
          <Button size="sm" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save', 'Save')}
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={history === null ? loadHistory : () => setHistory(null)}>
          <History className="w-4 h-4 mr-1" />
          {t('programs.statusHistory', 'Status history')}
        </Button>
        {!readOnly && (
          <Button size="sm" variant="ghost" className="ml-auto text-red-600" onClick={remove}>
            <Trash2 className="w-4 h-4 mr-1" />
            {t('common.remove', 'Remove')}
          </Button>
        )}
      </div>

      {history !== null && (
        <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
          {history.length === 0 ? (
            <p className="text-xs text-gray-400">{t('programs.noHistory', 'No status changes recorded.')}</p>
          ) : (
            <ul className="space-y-1 text-xs">
              {history.map((h) => (
                <li key={h.id} className="flex flex-wrap gap-2 text-gray-600 dark:text-gray-400">
                  <span className="font-mono">{formatDateOnly(h.changed_at)}</span>
                  <span>
                    {h.from_status || '—'} → <strong>{h.to_status || '—'}</strong>
                  </span>
                  {/* A cron source with no author is a machine decision, not a
                      promotora's. Say so, rather than showing a blank name. */}
                  <span className="text-gray-400">
                    {h.source.startsWith('cron:')
                      ? `${t('programs.byAutomation', 'by automation')} (${h.source})`
                      : h.changed_by_name || t('programs.byStaff', 'by staff')}
                  </span>
                  {h.reason && <span className="italic">{h.reason}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
export default function MemberProgramsTab({ memberId, readOnly = false }: MemberProgramsTabProps) {
  const { t } = useAppTranslation();
  const [enrollments, setEnrollments] = useState<ProgramEnrollment[]>([]);
  const [categories, setCategories] = useState<EnrollmentFieldCategory[]>([]);
  const [statusConfigs, setStatusConfigs] = useState<ProgramStatusConfig[]>([]);
  const [programs, setPrograms] = useState<EnrollableProgram[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addProgramId, setAddProgramId] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    try {
      const [rows, cfg, statuses, progs] = await Promise.all([
        programEnrollmentsAPI.listForMember(memberId),
        programEnrollmentsAPI.getFieldConfig(),
        programEnrollmentsAPI.getStatusConfig(),
        programEnrollmentsAPI.getPrograms(),
      ]);
      if (cancelled) return;
      setEnrollments(rows);
      setCategories(cfg.categories || []);
      setStatusConfigs(statuses);
      setPrograms(progs);
    } catch (e: any) {
      if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'Could not load programs');
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  useEffect(() => {
    void load();
  }, [load]);

  const enrolledProgramIds = useMemo(() => new Set(enrollments.map((e) => e.program_id)), [enrollments]);
  // One row per member x program is the model (the table is UNIQUE on the pair),
  // so a program already listed is not offerable again.
  const addable = programs.filter((p) => !enrolledProgramIds.has(p.id));

  const addEnrollment = async () => {
    if (!addProgramId) return;
    setAdding(true);
    setError(null);
    try {
      const created = await programEnrollmentsAPI.create(memberId, { program_id: Number(addProgramId) });
      setEnrollments((rows) => [...rows, created].sort((a, b) => (a.program_name || '').localeCompare(b.program_name || '')));
      setAddProgramId('');
      setExpanded(created.id);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not add program');
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('common.loading', 'Loading…')}
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-3 flex items-start gap-2 text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {categories.length === 0 && (
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          {t(
            'programs.noEnrollmentFields',
            'No program-enrollment fields are configured for this organization. Rows will show status and assignment only.',
          )}
        </p>
      )}

      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <tr>
                <th className="w-8" />
                <th className="px-3 py-2">{t('programs.program', 'Program')}</th>
                <th className="px-3 py-2">{t('programs.plan', 'Plan')}</th>
                <th className="px-3 py-2">{t('programs.status', 'Status')}</th>
                {/* The column labels the staff member assigned to the program
                    (e.assigned_to_name) — whoever that is in this org. It must
                    not hard-code one org's role terminology ("Promotora" leaked
                    to every org here, wrong for e.g. Maui LTRG — TRUS-421); the
                    org-neutral "Assigned to" is accurate everywhere. */}
                <th className="px-3 py-2">{t('programs.assignedTo', 'Assigned to')}</th>
                <th className="px-3 py-2">{t('programs.units', 'Units used / authorized')}</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-gray-500 dark:text-gray-400">
                    {t('programs.noEnrollments', 'This member is not enrolled in, or interested in, any program yet.')}
                  </td>
                </tr>
              )}
              {enrollments.map((e) => {
                const isOpen = expanded === e.id;
                return (
                  <React.Fragment key={e.id}>
                    <tr
                      className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                      onClick={() => setExpanded(isOpen ? null : e.id)}
                    >
                      <td className="pl-3 text-gray-400">
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">{e.program_name || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{e.managed_care_plan || '—'}</td>
                      <td className="px-3 py-2">
                        <Badge variant={statusVariant(e)} size="sm">
                          {/* A blank status is not missing data: it is the workbook's
                              "interested but never enrolled" state. */}
                          {e.status_label ||
                            (e.interest_shown_date
                              ? t('programs.interestShown', 'Interest shown')
                              : t('programs.noStatus', 'No status'))}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{e.assigned_to_name || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                        {e.units_authorized === null && e.units_used === null
                          ? '—'
                          : `${e.units_used ?? 0} / ${e.units_authorized ?? '—'}`}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={6} className="p-0">
                          <EnrollmentDetail
                            enrollment={e}
                            categories={categories}
                            statusConfig={statusConfigs.find((s) => s.program_id === e.program_id)}
                            readOnly={readOnly}
                            onSaved={(updated) =>
                              setEnrollments((rows) => rows.map((r) => (r.id === updated.id ? updated : r)))
                            }
                            onDeleted={(id) => {
                              setEnrollments((rows) => rows.filter((r) => r.id !== id));
                              setExpanded(null);
                            }}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {!readOnly && addable.length > 0 && (
        <div className="mt-3 flex items-end gap-2">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              {t('programs.addProgram', 'Add a program')}
            </label>
            <select className={inputClass} value={addProgramId} onChange={(e) => setAddProgramId(e.target.value)}>
              <option value="">—</option>
              {addable.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <Button size="sm" variant="secondary" disabled={!addProgramId || adding} onClick={addEnrollment}>
            <Plus className="w-4 h-4 mr-1" />
            {t('common.add', 'Add')}
          </Button>
        </div>
      )}
    </div>
  );
}
